import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createElement } from 'react'
import { authApi, type AuthUser } from '../api/auth'
import { setTokenGetter } from '../api/client'

const REFRESH_TOKEN_KEY = 'shioaji_refresh_token'

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const accessTokenRef = useRef<string | null>(null)

  // Wire the API client to always read the latest access token from memory.
  useEffect(() => {
    setTokenGetter(() => accessTokenRef.current)
  }, [])

  // Restore a session from the stored refresh token (shared by the normal and
  // the /api/auth/mode-failed paths — was duplicated verbatim).
  const restoreSession = useCallback(() => {
    const rtoken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!rtoken) { setIsLoading(false); return }
    authApi
      .refresh(rtoken)
      .then(({ access_token }) => {
        accessTokenRef.current = access_token
        const payload = _decodeJwt(access_token)
        setUser({ id: payload.sub, username: payload.username ?? '', role: payload.role ?? 'admin' })
      })
      .catch(() => { localStorage.removeItem(REFRESH_TOKEN_KEY) })
      .finally(() => setIsLoading(false))
  }, [])

  // On mount: check auth mode first, then restore session.
  useEffect(() => {
    fetch('/api/auth/mode')
      .then(r => r.json())
      .then((mode: { auth_required: boolean; user?: AuthUser }) => {
        if (!mode.auth_required && mode.user) {
          // Local dev mode: grant guest session without DB.
          accessTokenRef.current = 'local-no-auth'
          setTokenGetter(() => null)   // don't send Bearer header for local
          setUser(mode.user)
          setIsLoading(false)
          return
        }
        restoreSession()
      })
      .catch(() => { restoreSession() })  // mode probe failed → try normal auth
  }, [restoreSession])

  const login = useCallback(async (username: string, password: string) => {
    const { access_token, refresh_token, user: u } = await authApi.login(username, password)
    accessTokenRef.current = access_token
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token)
    setUser(u)
  }, [])

  const logout = useCallback(async () => {
    const rtoken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (rtoken) {
      try { await authApi.logout(rtoken) } catch { /* best effort */ }
    }
    accessTokenRef.current = null
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    setUser(null)
  }, [])

  const value: AuthState = { user, isLoading, login, logout }
  return createElement(AuthContext.Provider, { value }, children)
}

export function _decodeJwt(token: string): Record<string, string> {
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return {}
  }
}
